import { camelize, camelizeKeys } from "humps";
import cloneDeep from "lodash/cloneDeep";
import find from "lodash/find";
import pick from "lodash/pick";
import pickBy from "lodash/pickBy";
import sortBy from "lodash/sortBy";
import { CustomValuesEditField } from "PFApp/components/custom_values_edit_field";
import { getCanEditCustomFieldData } from "PFApp/components/custom_values_edit_field/custom_values_edit_field.utils";
import { LegacyForm } from "PFApp/components/form/legacy_form";
import { buildProfilesOptionsFetcher } from "PFApp/helpers/options/profiles_options";
import ProfileEditPanel from "PFApp/profiles/edit/profile_edit_panel";
import { getProfileSectionText } from "PFApp/profiles/edit/profile_edit_section_titles";
import { AdminInfo } from "PFApp/profiles/edit/sections/admin_info";
import css from "PFApp/profiles/edit/sections/section.module.scss";
import PROFILE_SECTIONS from "PFApp/profiles/profile_sections";
import { Inline, Stack } from "PFComponents/containers";
import { Select } from "PFComponents/select";
import { InputFieldSet } from "PFComponents/text/input_field_set";
import { Typography } from "PFComponents/typography";
import { getPermittedTypes, loadForSections } from "PFCore/helpers/custom_type";
import {
  customTypesAccessLevelsAgainstProfile,
  ownProfileCustomTypesAccessLevels
} from "PFCore/helpers/custom_types";
import {
  getProfileCustomTypesAccessLevel,
  isProfileFieldPermitted
} from "PFCore/helpers/profile_permissions";
import { isFeatureEnabled } from "PFCore/helpers/use_is_feature_enabled";
import { editProfile } from "PFCore/services/admin/profiles/edit_profile";
import { AccessLevel, FeatureFlag } from "PFTypes";
import PropTypes from "prop-types";
import React, { Component } from "react";
import { withTranslation } from "react-i18next";

class DetailsSection extends Component {
  constructor(props) {
    super(props);

    const loadCustomTypes = (section, whiteList) => {
      const ctAccessLevels = props.adminPage
        ? customTypesAccessLevelsAgainstProfile(props.profile, props.currentProfile)
        : ownProfileCustomTypesAccessLevels(props.currentProfile);

      const sectionTypes = loadForSections(section, props.myAccountInfo.custom_types);
      const types = sortBy(
        getPermittedTypes(sectionTypes, { ctAccessLevels, ro: true, rw: true }),
        "display_as"
      );
      types.forEach(({ name }) => (whiteList[name] = true));

      return types;
    };

    const whiteList = {};

    this.customTypes = loadCustomTypes(PROFILE_SECTIONS.profile_basic_info, whiteList);
    this.expertiseCTs = loadCustomTypes(PROFILE_SECTIONS.profile_expertise, whiteList);
    this.privateCTs = loadCustomTypes(PROFILE_SECTIONS.profile_private_fields, whiteList);

    // Profile Fields permissions setup
    if (this.isFieldPermitted("firstAndLastName", AccessLevel.ReadOnly)) {
      whiteList["firstName"] = true;
      whiteList["lastName"] = true;
    }
    if (this.isFieldPermitted("summary", AccessLevel.ReadOnly)) {
      whiteList["summary"] = true;
    }

    this.state = {
      errors: {},
      formData: {
        ...pick(cloneDeep(this.props.profile), ...Object.keys(camelizeKeys(whiteList))),
        supervisorId: this.props.profile.supervisor?.id
      },
      isFormDirty: false
    };
  }

  isFieldPermitted = (field, level = AccessLevel.ReadWrite) => {
    const { profile, permissionsGroup } = this.props;

    return isProfileFieldPermitted(permissionsGroup, profile.permissionsGroup.id, field, level);
  };

  isCustomTypePermitted = (customTypeId, level = AccessLevel.ReadWrite) => {
    const { profile, permissionsGroup } = this.props;

    const customTypeAccessLevel = getProfileCustomTypesAccessLevel(permissionsGroup, profile)[customTypeId];

    return customTypeAccessLevel === level || customTypeAccessLevel === AccessLevel.ReadWrite;
  };

  // Field should be displayed:
  // - if permissions are met (at least read-only permissions are granted for given field)
  // - if editing/displaying own profile and some content is placed inside the field (then even if the field
  //   is `hidden` we show it's content (as read-only then))
  shouldBeDisplayed = (field) => {
    // if we have at least read-only permissions
    if (this.isFieldPermitted(field, AccessLevel.ReadOnly)) {
      return true;
    }

    // if some content is set and editing own profile
    if (this.editingOwnProfile() && !!this.props.profile.summary === true) {
      return true;
    }

    return false;
  };

  editingOwnProfile = () => this.props.currentProfile.id === this.props.profile.id;

  setFormData = (formData) =>
    this.setState((prevState) => ({
      isFormDirty: true,
      formData: {
        ...prevState.formData,
        ...formData
      }
    }));

  handleSuccess = () => {
    this.setState({ errors: {}, isFormDirty: false });
    this.props.handleProfileUpdate();
  };

  handleError = (errors) => {
    this.setState({ errors: errors, isFormDirty: false });
  };

  handleEditProfile = () => {
    const { currentProfile } = this.props;

    const permittedFormData = pickBy(this.state.formData, (_, key) => {
      if (["firstName", "lastName"].includes(key)) {
        return this.isFieldPermitted("firstAndLastName", AccessLevel.ReadWrite);
      }

      if (key === "summary") {
        return this.isFieldPermitted("summary", AccessLevel.ReadWrite);
      }

      const customType = find(currentProfile.customTypes, { name: key });

      if (!customType) {
        return true;
      }

      return this.isCustomTypePermitted(customType.id, AccessLevel.ReadWrite);
    });

    return editProfile(this.props.profile.id, permittedFormData);
  };

  render() {
    const { currentAccount, adminPage, profile, t, currentProfile } = this.props;
    const isEnabled = isFeatureEnabled(currentAccount);

    const { errors } = this.state;
    const {
      config: {
        profile: { top_skills_limit: coreSkillsLimit, developmental_skills_limit: developmentalSkillsLimit }
      }
    } = currentAccount;

    const customTypeToComponent = (customType, qaIdPrefix) => {
      const { name } = customType;
      const camelizedName = camelize(name);
      const values = this.state.formData[camelizedName] || [];
      let topSkillsTag = null;
      let developmentalSkillsTag = null;

      if (name === "skills") {
        const topSkills = values.filter((skill) => skill.top);
        const developmentalSkills = values.filter((skill) => skill.developmental);

        const { locked, lockedTip } = getCanEditCustomFieldData({
          adminPage,
          customType,
          profile: profile,
          position: null,
          currentProfile,
          t: (key) => t(key, { ns: "translation" })
        });

        topSkillsTag = (
          <Select
            qaId={`${qaIdPrefix}-top-skills`}
            cacheTime={0}
            label={t("profiles:edit.sections.details.coreSkills")}
            tip={t("profiles:edit.sections.details.coreSkillsTip", { count: coreSkillsLimit })}
            locked={locked}
            lockedTip={lockedTip}
            value={topSkills.map((skill) => skill.id)}
            multiple={customType.kind === "multiple"}
            limit={coreSkillsLimit}
            options={values.map((item) => ({
              id: item.id,
              value: item.text,
              original: item
            }))}
            onChange={(options) => {
              const newCoreIds = options.map((opt) => opt.id);
              const newValues = values.map((skill) => ({
                ...skill,
                top: newCoreIds.includes(skill.id)
              }));

              this.setFormData({ skills: newValues });
            }}
          />
        );

        developmentalSkillsTag = (
          <Select
            qaId={`${qaIdPrefix}-developmental-skills`}
            cacheTime={0}
            label={t("profiles:edit.sections.details.developmentalSkills")}
            tip={t("profiles:edit.sections.details.developmentalSkillsTip", {
              count: developmentalSkillsLimit
            })}
            locked={locked}
            lockedTip={lockedTip}
            value={developmentalSkills.map((skill) => skill.id)}
            multiple={customType.kind === "multiple"}
            limit={developmentalSkillsLimit}
            options={values.map((item) => ({
              id: item.id,
              value: item.text,
              original: item
            }))}
            onChange={(options) => {
              const newDevelopmentalIds = options.map((opt) => opt.id);
              const newValues = values.map((skill) => ({
                ...skill,
                developmental: newDevelopmentalIds.includes(skill.id)
              }));

              this.setFormData({ skills: newValues });
            }}
          />
        );
      }

      return (
        <React.Fragment key={camelizedName}>
          <CustomValuesEditField
            customType={customType}
            values={values}
            qaIdPrefix={qaIdPrefix}
            errors={errors[camelizedName]}
            adminPage={adminPage}
            profile={profile}
            classes={{ root: css.customValueField }}
            letClear
            perPage={250}
            handleChange={(items) => {
              const newValues = items.map(
                (item) =>
                  find(values, { id: item.id }) ||
                  pick(item, "id", "global_id", "type", "text", "value", "expiry_date")
              );

              this.setFormData({
                [camelizedName]: newValues
              });
            }}
          />
          {topSkillsTag}
          {developmentalSkillsTag}
        </React.Fragment>
      );
    };

    return (
      <ProfileEditPanel title={getProfileSectionText("details")}>
        <LegacyForm
          isDirty={this.state.isFormDirty}
          onSuccess={this.handleSuccess}
          onError={this.handleError}
          onSubmit={this.handleEditProfile}
          qaIdPrefix="profile-edit-update"
          growlErrors
          blockNavigationOnChange
        >
          <AdminInfo profile={profile} />
          <Inline>
            <InputFieldSet
              label={t("profiles:edit.sections.details.firstName")}
              qaId="firstName"
              error={errors.firstName}
              locked={!this.isFieldPermitted("firstAndLastName")}
              lockedTip={t("profiles:edit.sections.details.noPermsTip")}
              value={this.state.formData.firstName}
              onChange={(firstName) => this.setFormData({ firstName })}
              className={css.nameField}
            />
            <InputFieldSet
              label={t("profiles:edit.sections.details.lastName")}
              qaId="lastName"
              error={errors.lastName}
              locked={!this.isFieldPermitted("firstAndLastName")}
              lockedTip={t("profiles:edit.sections.details.noPermsTip")}
              value={this.state.formData.lastName}
              onChange={(lastName) => this.setFormData({ lastName })}
              className={css.nameField}
            />
          </Inline>
          <Stack>
            {this.customTypes.map((item) => customTypeToComponent(item, "profile-basic-info"))}
            {this.shouldBeDisplayed("summary") && (
              <InputFieldSet
                inputType="textarea"
                label={t("profiles:edit.sections.details.shortBio")}
                qaId="summary"
                error={errors.summary}
                locked={!this.isFieldPermitted("summary")}
                lockedTip={t("profiles:edit.sections.details.noPermsTip")}
                value={this.state.formData.summary}
                onChange={(summary) => this.setFormData({ summary })}
              />
            )}
            {isEnabled(FeatureFlag.ReportsTo) && (
              <div data-qa-id="profile-edit-current-position-reports-to">
                <Select
                  value={this.state.formData.supervisorId}
                  label={t("profiles:edit.sections.details.reportsTo")}
                  fetchOptions={buildProfilesOptionsFetcher({
                    excludeProfileId: currentProfile.id,
                    renderDisplayElement: false
                  })}
                  onChange={(option) => {
                    this.setFormData({
                      supervisorId: option ? option.id : null
                    });
                  }}
                  onClear={
                    this.state.formData.supervisorId
                      ? () => this.setFormData({ supervisorId: null })
                      : undefined
                  }
                />
              </div>
            )}
            {!!this.expertiseCTs.length && (
              <Typography variant="h3" withMargin>
                {t("profiles:edit.sections.details.expertise")}
              </Typography>
            )}
            <Stack>
              {!!this.expertiseCTs.length &&
                this.expertiseCTs.map((item) => customTypeToComponent(item, "profile-expertise"))}
            </Stack>

            {!!this.privateCTs.length && (
              <Typography variant="h3" withMargin>
                {t("profiles:edit.sections.details.fieldsOnlyYouCanSee")}
              </Typography>
            )}
            <Stack>
              {!!this.privateCTs.length &&
                this.privateCTs.map((item) => customTypeToComponent(item, "profile-expertise"))}
            </Stack>
          </Stack>
        </LegacyForm>
      </ProfileEditPanel>
    );
  }
}

const DetailsSectionWithTranslation = withTranslation(["profiles", "translation"])(DetailsSection);

export default DetailsSectionWithTranslation;

DetailsSection.propTypes = {
  profile: PropTypes.any.isRequired,
  currentProfile: PropTypes.any.isRequired,
  currentAccount: PropTypes.any.isRequired,
  adminPage: PropTypes.bool.isRequired,
  handleProfileUpdate: PropTypes.func,
  permissionsGroup: PropTypes.any,
  t: PropTypes.func
};
